/**
 * @license
 * Copyright Google Inc. All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */


import {isPromise} from '../src/util/lang';

import {Inject, Injectable, InjectionToken, Optional} from './di';
/**
 * A function that will be executed when an application is initialized.
 * \@experimental
 */
export const APP_INITIALIZER = new InjectionToken<Array<() => void>>('Application Initializer');
/**
 * A class that reflects the state of running {\@link APP_INITIALIZER}s.
 * 
 * \@experimental
 */
export class ApplicationInitStatus {
private resolve: Function;
private reject: Function;
private initialized = false;
private _donePromise: Promise<any>;
private _done = false;
/**
 * @param {?} appInits
 */
constructor(
private appInits: (() => any)[]) {
    this._donePromise = new Promise((res, rej) => {
      this.resolve = res;
      this.reject = rej;
    });
  }
/**
 * \@internal
 * @return {?}
 */
runInitializers() {
    if (this.initialized) {
      return;
    }

    const /** @type {?} */ asyncInitPromises: Promise<any>[] = [];

    const /** @type {?} */ complete = () => {
      this._done = true;
      this.resolve();
    };

    if (this.appInits) {
      for (let /** @type {?} */ i = 0; i < this.appInits.length; i++) {
        const /** @type {?} */ initResult = this.appInits[i]();
        if (isPromise(initResult)) {
          asyncInitPromises.push(initResult);
        }
      }
    }

    Promise.all(asyncInitPromises).then(() => { complete(); }).catch(e => { this.reject(e); });

    if (asyncInitPromises.length === 0) {
      complete();
    }
    this.initialized = true;
  }
/**
 * @return {?}
 */
get done(): boolean { return this._done; }
/**
 * @return {?}
 */
get donePromise(): Promise<any> { return this._donePromise; }
static decorators: DecoratorInvocation[] = [
{ type: Injectable },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: Array, decorators: [{ type: Inject, args: [APP_INITIALIZER, ] }, { type: Optional }, ]},
];
}

function ApplicationInitStatus_tsickle_Closure_declarations() {
/** @type {?} */
ApplicationInitStatus.decorators;
/**
 * @nocollapse
 * @type {?}
 */
ApplicationInitStatus.ctorParameters;
/** @type {?} */
ApplicationInitStatus.prototype.resolve;
/** @type {?} */
ApplicationInitStatus.prototype.reject;
/** @type {?} */
ApplicationInitStatus.prototype.initialized;
/** @type {?} */
ApplicationInitStatus.prototype._donePromise;
/** @type {?} */
ApplicationInitStatus.prototype._done;
/** @type {?} */
ApplicationInitStatus.prototype.appInits;
}


interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
